import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { formatPercentage, calculateMetricsFromTasks } from "@/lib/utils";
import type { Metrics, Task } from "@/types/dashboard";

interface SuccessFailurePieProps {
  metrics: Metrics | null;
  tasks: Record<string, Task>;
  type?: "score" | "sql";
  totalQuestions?: number;
}

// Custom tooltip with glassmorphism
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="glass-tooltip">
        <p className="font-semibold text-amber-200">{data.name}</p>
        <p className="text-lg font-bold bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
          {data.value} ({formatPercentage(data.payload.percentage)})
        </p>
      </div>
    );
  }
  return null;
};

export function SuccessFailurePie({ metrics: propMetrics, tasks, type = "score", totalQuestions }: SuccessFailurePieProps) {
  // Calculate metrics from tasks (single source of truth)
  const metrics = useMemo(() => {
    if (tasks && Object.keys(tasks).length > 0) {
      return calculateMetricsFromTasks(tasks, totalQuestions);
    }
    return propMetrics;
  }, [tasks, totalQuestions, propMetrics]);

  const chartData = useMemo(() => {
    if (!metrics) return [];

    if (type === "sql") {
      // SQL Correctness Pie Chart
      const completed = metrics.completed;
      const failed = metrics.failed;
      const pending = metrics.queued + metrics.running;
      const total = completed + failed + pending;

      if (total === 0) return [];

      const sqlValid = (metrics.sql_success_rate / 100) * (completed + failed);
      const sqlInvalid = (completed + failed) - sqlValid;

      const data = [
        { name: "Valid SQL", value: Math.round(sqlValid), percentage: (sqlValid / total) * 100, color: "#22c55e" },
        { name: "Invalid SQL", value: Math.round(sqlInvalid), percentage: (sqlInvalid / total) * 100, color: "#ef4444" },
      ];

      if (pending > 0) {
        data.push({ name: "Pending", value: pending, percentage: (pending / total) * 100, color: "#6b7280" });
      }

      return data.filter(item => item.value > 0);
    } else {
      // Score Distribution Pie Chart
      const scores = metrics.scores || [];
      const completed = scores.length;
      const pending = metrics.queued + metrics.running;
      const total = completed + pending;

      if (total === 0) return [];

      const excellent = scores.filter(s => s >= 4.5).length;
      const good = scores.filter(s => s >= 3.5 && s < 4.5).length;
      const fair = scores.filter(s => s >= 2.5 && s < 3.5).length;
      const poor = scores.filter(s => s < 2.5).length;

      const data = [
        { name: "Excellent (4.5-5.0)", value: excellent, percentage: (excellent / total) * 100, color: "#22c55e" },
        { name: "Good (3.5-4.5)", value: good, percentage: (good / total) * 100, color: "#eab308" },
        { name: "Fair (2.5-3.5)", value: fair, percentage: (fair / total) * 100, color: "#f97316" },
        { name: "Poor (< 2.5)", value: poor, percentage: (poor / total) * 100, color: "#ef4444" },
      ];

      if (pending > 0) {
        data.push({ name: "Pending", value: pending, percentage: (pending / total) * 100, color: "#6b7280" });
      }

      return data.filter(item => item.value > 0);
    }
  }, [metrics, type]);

  if (!metrics || chartData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Waiting for evaluation data...
      </div>
    );
  }

  const title = type === "sql" ? "SQL Correctness" : "Score Distribution";
  const subtitle = type === "sql"
    ? `${formatPercentage(metrics.sql_success_rate)} valid`
    : `${metrics.scores.length} questions`;

  return (
    <div className="h-full w-full flex flex-col">
      <div className="mb-3">
        <h3 className="text-base font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ percentage }) => percentage > 5 ? `${formatPercentage(percentage, 0)}` : ''}
              outerRadius="70%"
              fill="#8884d8"
              dataKey="value"
              stroke="rgba(168, 139, 250, 0.3)"
              strokeWidth={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={type === "sql" ? 30 : 60}
              iconType="circle"
              wrapperStyle={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}
              formatter={(value) => <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
